import React, { useRef } from "react";
import { capitalize } from "lodash";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Button, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import locale, { Locale } from "@bonliva-crm/locale";
import { Option } from "@common/Select/index";
import Select, { SelectProps } from "@common/Select/Select";

export { Option };

export enum SelectType {
  ANY = "ANY",
  AND = "AND",
  ONLY = "ONLY"
}

type SelectTypeAlign = "left" | "right";

type Props = {
  clearable?: boolean;
  disabled?: boolean;
  grouped?: boolean;
  groupByField?: string;
  label: string;
  onChange: (v: any) => void;
  onTypeSelect?: (type: SelectType) => void;
  options: string[] | Option[];
  selectType?: SelectType;
  selectTypeAlign?: SelectTypeAlign;
  allowedTypes?: SelectType[];
  sortOptions?: SelectProps["sortOptions"];
  sortGroups?: boolean;
  onAllSelect?: (options) => void;
  testName?: string;
  value: any;
  renderMenu?: SelectProps["renderMenu"];
  className?: string;
  required?: boolean;
  error?: boolean;
};

export const SearchSelect = (props: Props) => {
  const {
    onAllSelect,
    grouped,
    label,
    options,
    onTypeSelect,
    selectType = SelectType.ANY,
    sortOptions = true,
    testName,
    className,
    allowedTypes = [SelectType.ANY, SelectType.AND, SelectType.ONLY],
    selectTypeAlign,
    ...rest
  } = props;

  const reactSelectMuiRef = useRef<any>(null);
  const classes = useStyles({});

  const multi = props.value instanceof Array;

  const _options =
    options && options.length > 0 && typeof options[0] === "string"
      ? (props.options as string[]).toLabelValue()
      : (options as Option[]);

  const selectTypeComponent = props.onTypeSelect && (
    <ToggleButtonGroup
      className={classes.typeSelect}
      value={selectType}
      exclusive
      onChange={(_, v) => props.onTypeSelect(v ?? SelectType.ANY)}
    >
      {allowedTypes.map((v) => {
        const key: keyof Locale = `Search${
          capitalize(v) as Capitalize<Lowercase<typeof v>>
        }`;

        return (
          <ToggleButton key={v} value={v}>
            {locale[key]}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );

  const renderMenu = (menu) => {
    if (selectTypeComponent) {
      return (
        <Paper className={classes.beforeMenu}>
          <div
            className={classes.typeSelectWrapper}
            style={{
              justifyContent: selectTypeAlign === "left" ? "flex-start" : "flex-end"
            }}
          >
            {selectTypeComponent}
          </div>
          <div>{menu}</div>
        </Paper>
      );
    }

    if (onAllSelect) {
      return (
        <Paper className={classes.beforeMenu}>
          <div>
            <Button
              style={{ margin: "8px" }}
              size="small"
              variant="outlined"
              onClick={() => reactSelectMuiRef.current.selectRef?.current.selectVisible()}
            >
              {locale.SelectAll}
            </Button>
          </div>

          <div>{menu}</div>
        </Paper>
      );
    }

    return menu;
  };

  return multi ? (
    <Select
      fullWidth
      label={locale.SelectSomething(label)}
      options={_options}
      multi
      sortOptions={sortOptions}
      hideSameNameChildInGroups
      optionType="checkbox"
      groupBy={grouped ? "parent" : null}
      name={testName || props["data-test"]}
      renderMenu={renderMenu}
      onAllSelect={onAllSelect}
      className={className}
      innerRef={(ref) => (reactSelectMuiRef.current = ref)}
      {...rest}
    />
  ) : (
    <Select
      fullWidth
      label={locale.SelectSomething(label)}
      options={_options}
      sortOptions={sortOptions}
      name={testName || props["data-test"]}
      renderMenu={renderMenu}
      className={className}
      {...rest}
    />
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  typeSelect: {
    display: "inline-block",
    borderRadius: 4,
    border: `1px solid ${theme.separatorColor}`,
    boxShadow: "none"
  },

  typeSelectWrapper: {
    display: "flex",
    padding: 8,
    textAlign: "right",
    borderBottom: `1px solid ${theme.separatorColor}`
  },

  beforeMenu: {
    "& .Select-menu-dropdown": {
      boxShadow: "none !important"
    }
  }
}));
